import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  List,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { LabelMappingSection } from "./LabelMappingSection";
import { TokensSection } from "./TokensSection";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type {
  ConnectedReposResponse,
  ForgejoReposResponse,
  ForgejoStatusResponse,
} from "./api";

// Props для connected-режима Forgejo-секции. Все мутации / handler'ы /
// state живут в родительском `ForgejoSection.tsx`; этот компонент —
// чистая визуализация.
interface ForgejoConnectedViewProps {
  readonly status: ForgejoStatusResponse;
  readonly reposQuery: UseQueryResult<ForgejoReposResponse, Error>;
  readonly connectedReposQuery: UseQueryResult<ConnectedReposResponse, Error>;
  readonly webhookUrl: string | null;
  readonly disconnectInfo: string | null;
  readonly submitError: string | null;
  readonly disconnectRepoPendingRepo: string | undefined;
  readonly disconnectRepoIsPending: boolean;
  readonly connectRepoPendingRepo: string | undefined;
  readonly connectRepoIsPending: boolean;
  readonly onRequestDisconnectRepo: (repo: string) => void;
  readonly onConnectRepo: (repo: string) => void;
}

export function ForgejoConnectedView(
  props: ForgejoConnectedViewProps,
): ReactElement {
  const { t } = useTranslation();
  const {
    status,
    reposQuery,
    connectedReposQuery,
    webhookUrl,
    disconnectInfo,
    submitError,
    disconnectRepoPendingRepo,
    disconnectRepoIsPending,
    connectRepoPendingRepo,
    connectRepoIsPending,
    onRequestDisconnectRepo,
    onConnectRepo,
  } = props;

  return (
    <Stack gap="md">
      <Alert color="green">
        {t("connections.connectedAs", { host: status.host_url ?? "" })}
      </Alert>

      {webhookUrl !== null ? (
        <Alert color="blue">
          {t("connections.webhookRegisteredAt", { url: webhookUrl })}
        </Alert>
      ) : null}

      {disconnectInfo !== null ? (
        <Alert color="blue">{disconnectInfo}</Alert>
      ) : null}

      {submitError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {submitError}
        </Alert>
      ) : null}

      <Title order={3}>{t("connections.repoListTitle")}</Title>

      {reposQuery.isPending ? (
        <Loader />
      ) : reposQuery.isError ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      ) : reposQuery.data.repos.length === 0 ? (
        <Text c="dimmed">{t("connections.reposEmpty")}</Text>
      ) : (
        <Table withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("connections.colName")}</Table.Th>
              <Table.Th>{t("connections.colPrivate")}</Table.Th>
              <Table.Th>{t("connections.colStatus")}</Table.Th>
              <Table.Th>{t("connections.colAction")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {reposQuery.data.repos.map((repo) => (
              <Table.Tr key={repo.full_name}>
                <Table.Td>
                  <a href={repo.html_url} target="_blank" rel="noreferrer">
                    {repo.full_name}
                  </a>
                </Table.Td>
                <Table.Td>
                  {repo.private
                    ? t("connections.privateYes")
                    : t("connections.privateNo")}
                </Table.Td>
                <Table.Td>
                  {repo.connected ? (
                    <Badge color="green">
                      {t("connections.repoConnected")}
                    </Badge>
                  ) : (
                    <Text c="dimmed" size="sm">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {repo.connected ? (
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      aria-label={t(
                        "connections.forgejo.disconnectRepo.button",
                      )}
                      onClick={() => {
                        onRequestDisconnectRepo(repo.full_name);
                      }}
                      loading={
                        disconnectRepoIsPending &&
                        disconnectRepoPendingRepo === repo.full_name
                      }
                    >
                      ×
                    </ActionIcon>
                  ) : (
                    <Button
                      size="xs"
                      onClick={() => {
                        onConnectRepo(repo.full_name);
                      }}
                      loading={
                        connectRepoIsPending &&
                        connectRepoPendingRepo === repo.full_name
                      }
                    >
                      {t("connections.repoConnect")}
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Title order={3}>{t("connections.connectedReposTitle")}</Title>

      {connectedReposQuery.isPending ? (
        <Loader />
      ) : connectedReposQuery.isError ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      ) : connectedReposQuery.data.repos.length === 0 ? (
        <Text c="dimmed">{t("connections.connectedReposEmpty")}</Text>
      ) : (
        <List>
          {connectedReposQuery.data.repos.map((name) => (
            <List.Item key={name}>
              <Group gap="sm">
                <Text component="span">{name}</Text>
                <Anchor
                  component={Link}
                  to={`/settings/repository-settings?repo=${name}`}
                >
                  {t("connections.repoSettingsLink")}
                </Anchor>
              </Group>
            </List.Item>
          ))}
        </List>
      )}

      <TokensSection />

      <LabelMappingSection />

      <Group justify="flex-end" />
    </Stack>
  );
}
